import { createFileRoute, Link } from "@tanstack/react-router";
import { useMemo, useState } from "react";
import { withMemberGate } from "@/lib/with-member-gate";
import {
  ArrowUpRight,
  Heart,
  Lock,
  Mail,
  Palette as PaletteIcon,
  Sparkles,
  Sun,
} from "lucide-react";
import { SwipeRail } from "@/components/SwipeRail";
import { FeelingCheckIn } from "@/components/inner-child/FeelingCheckIn";
import { LettersSection, SectionHead } from "@/components/inner-child/LettersSection";
import { ChooserList } from "@/components/inner-child/ChooserList";
import { ExercisePanel } from "@/components/inner-child/ExercisePanel";
import { ReflectionCard } from "@/components/inner-child/ReflectionCard";
import { PersonalNote } from "@/components/inner-child/PersonalNote";
import { useInnerChild } from "@/components/inner-child/useInnerChild";
import {
  COMFORT_ITEMS,
  EXERCISES,
  JOY_ITEMS,
  REFLECTION_CARDS,
  type Exercise,
} from "@/lib/inner-child";
import heroImage from "@/assets/inner-child/meet-your-inner-child.jpg";
import { JournalGuide } from "@/components/JournalGuide";
import { SavedExpressions } from "@/components/creative-expression/SavedExpressions";

export const Route = createFileRoute("/inner-child")({
  head: () => ({
    meta: [
      { name: "robots", content: "noindex" },
      { title: "Inner Child Work — Recovery Trauma™" },
      {
        name: "description",
        content:
          "Gently reconnect with younger parts of yourself: guided inner child exercises, private letters, reparenting practice, childhood joy and reflection prompts.",
      },
      { property: "og:title", content: "Inner Child Work — Recovery Trauma™" },
      {
        property: "og:description",
        content:
          "A warm, private space to reconnect with your younger self — letters, reflections, reparenting and childhood joy.",
      },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary_large_image" },
      { property: "og:url", content: "https://recoverytrauma.com/inner-child" },
    ],
    links: [{ rel: "canonical", href: "https://recoverytrauma.com/inner-child" }],
  }),
  component: withMemberGate(InnerChildPage, "Inner Child Work"),
});

const JUMP_CARDS = [
  {
    key: "letter",
    title: "Letter to My Younger Self",
    blurb: "Write something your younger self needed to hear.",
    palette: "pink" as const,
    doodle: "💌",
    href: "#letters",
  },
  {
    key: "joy",
    title: "Childhood Joy",
    blurb: "Reconnect with play, creativity and things that once brought comfort.",
    palette: "peach" as const,
    doodle: "🌈",
    href: "#childhood-joy",
  },
  {
    key: "comfort",
    title: "Comfort Menu",
    blurb: "Your own list of things that help you feel soothed and cared for.",
    palette: "purple" as const,
    doodle: "🕯️",
    href: "#comfort-menu",
  },
];

function InnerChildPage() {
  const [active, setActive] = useState<Exercise | null>(null);
  const { entries } = useInnerChild();

  // Soft, non-pressuring progress: how many different parts have been explored.
  const explored = useMemo(() => {
    const keys = new Set(
      entries
        .filter((e) => e.kind !== "check_in")
        .map((e) => `${e.kind}:${e.prompt_key ?? ""}`),
    );
    return keys.size;
  }, [entries]);

  const journeyDots = 5;
  const litDots = Math.min(journeyDots, explored);

  return (
    <div className="ic-page min-h-screen pb-24">
      <div className="mx-auto max-w-5xl px-4 pt-6 sm:px-6">
        <JournalGuide journal="innerChild" className="mt-4" />

        {/* Hero */}
        <section
          className="mt-4 grid items-center gap-8 rounded-[26px] border border-border/60 p-5 shadow-sm sm:p-7 md:grid-cols-[1.05fr_1fr]"
          style={{
            backgroundImage:
              "linear-gradient(150deg, var(--qa-peach-tint) 0%, var(--qa-rose-tint) 46%, var(--card) 100%)",
          }}
        >
          <div>
            <p
              className="inline-flex items-center gap-2 rounded-full bg-card px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.16em]"
              style={{ color: "var(--qa-peach-ink)" }}
            >
              <Sparkles className="h-3.5 w-3.5" aria-hidden /> Process · Inner Child Work
            </p>
            <h1 className="mt-3 font-display text-[2rem] leading-tight text-foreground sm:text-5xl">
              Meet Your Inner Child
            </h1>
            <p className="mt-3 max-w-xl text-[1.02rem] leading-relaxed text-foreground/80">
              Inner child work is an opportunity to gently reconnect with younger parts of
              yourself — the feelings, needs, memories and experiences that may still
              influence you today.
            </p>
            <div className="mt-6 flex flex-wrap gap-3">
              <a href="#check-in" className="btn-gold inline-flex items-center justify-center">
                Begin Gently
              </a>
              <a
                href="#what-is-it"
                className="inline-flex items-center gap-2 rounded-full border border-border bg-card px-5 py-2.5 text-sm font-medium text-foreground transition hover:border-gold/50"
              >
                What Is Inner Child Work?
              </a>
            </div>
            <p className="mt-4 inline-flex items-center gap-1.5 text-xs text-muted-foreground">
              <Lock className="h-3.5 w-3.5" aria-hidden /> Everything you write here stays private
              to your account.
            </p>
          </div>
          <div className="ic-hero-frame">
            <img
              src={heroImage}
              alt="An adult sitting quietly beside their younger self in a meadow at sunset"
              width={1024}
              height={1024}
              className="h-auto w-full rounded-[2rem]"
            />
          </div>
        </section>

        {/* Soft journey progress */}
        <section className="mt-8 rounded-3xl border border-border/60 bg-background/60 p-4 sm:p-5">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div>
              <p className="font-display text-lg text-foreground">Your Inner Child Journey</p>
              <p className="text-sm text-muted-foreground">
                {explored === 0
                  ? "Nothing to complete here — wander wherever feels kind."
                  : `${explored} reflection${explored === 1 ? "" : "s"} explored`}
              </p>
            </div>
            <div className="flex items-center gap-2" aria-hidden>
              {Array.from({ length: journeyDots }).map((_, i) => (
                <span
                  key={i}
                  className={`h-2.5 w-2.5 rounded-full ${
                    i < litDots ? "bg-[var(--gold-deep)]" : "border border-border bg-transparent"
                  }`}
                />
              ))}
            </div>
          </div>
        </section>

        {/* What is inner child work */}
        <section id="what-is-it" className="mt-10 scroll-mt-24">
          <SectionHead
            emoji="🌱"
            title="What Is Inner Child Work?"
            blurb="A way of listening to the younger parts of you that learned how to survive."
          />
          <div className="mt-4 grid gap-4 md:grid-cols-2">
            <div className="ic-paper rounded-[2rem] border border-border/60 p-5">
              <p className="text-[0.975rem] leading-relaxed text-foreground/90">
                Inner child work isn't about blaming anyone or reliving what happened. It's
                about noticing the needs, beliefs and protective habits that formed early —
                and offering them something different now: patience, warmth, and a bit more
                room to breathe.
              </p>
              <p className="mt-3 text-[0.975rem] leading-relaxed text-foreground/90">
                Some of it is tender. Some of it is playful. You get to choose which parts you
                approach today, and you can stop at any point.
              </p>
            </div>
            <PersonalNote>
              <p>
                For a long time I thought "inner child" sounded a bit soft — until I realised
                how much of my adult life was still being run by a girl who learned to make
                herself small and useful.
              </p>
              <p>
                What helped me wasn't going back into the worst of it. It was small, almost
                silly things: eating properly, letting myself like what I liked, and speaking
                to myself the way I'd speak to her. Go slowly. There's no prize for finishing.
              </p>
            </PersonalNote>
          </div>
        </section>

        {/* Emotional check-in */}
        <section id="check-in" className="mt-10 scroll-mt-24">
          <FeelingCheckIn />
        </section>

        {/* Toolkit */}
        <section id="toolkit" className="mt-12 scroll-mt-24">
          <SectionHead
            emoji="🧸"
            title="Inner Child Toolkit"
            blurb="Swipe through and open whatever feels possible today. Nothing here has to be finished."
          />
          <SwipeRail label="Inner Child Toolkit" className="mt-5">
            {EXERCISES.map((ex) => {
              const Icon = ex.icon;
              return (
                <button
                  key={ex.key}
                  type="button"
                  onClick={() => setActive(active?.key === ex.key ? null : ex)}
                  className={`assess-card assess-${ex.palette} w-[15.5rem] shrink-0 snap-start p-5 text-start sm:w-[17.5rem]`}
                >
                  <span className="assess-card__glow" aria-hidden />
                  <span className="assess-card__icon relative" aria-hidden>
                    <Icon className="h-5 w-5" />
                  </span>
                  <p className="relative mt-3 font-display text-lg leading-snug text-foreground">
                    {ex.title}
                  </p>
                  <p className="relative mt-1 text-sm text-muted-foreground">{ex.blurb}</p>
                </button>
              );
            })}
            {JUMP_CARDS.map((c) => (
              <a
                key={c.key}
                href={c.href}
                className={`assess-card assess-${c.palette} w-[15.5rem] shrink-0 snap-start p-5 text-start sm:w-[17.5rem]`}
              >
                <span className="assess-card__glow" aria-hidden />
                <span className="assess-card__icon relative" aria-hidden>
                  {c.doodle}
                </span>
                <p className="relative mt-3 font-display text-lg leading-snug text-foreground">
                  {c.title}
                </p>
                <p className="relative mt-1 text-sm text-muted-foreground">{c.blurb}</p>
              </a>
            ))}
          </SwipeRail>

          {active && <ExercisePanel exercise={active} onClose={() => setActive(null)} />}
        </section>

        <div className="mt-10">
          <ReflectionCard prompt={REFLECTION_CARDS[0]} palette="lavender" doodle="🪞" />
        </div>

        {/* Letters */}
        <div className="mt-12">
          <LettersSection />
        </div>

        {/* Creative */}
        <section id="creative" className="mt-12 scroll-mt-24">
          <SectionHead
            emoji="🎨"
            title="Creative Inner Child"
            blurb="Some things are easier drawn than said. These open in the Drawing Board you already have."
          />
          <SwipeRail label="Creative inner child prompts" className="mt-5">
            {[
              {
                to: "/art-board?a=childhood-memory",
                title: "Draw a childhood memory",
                blurb: "Anything you remember — ordinary or vivid.",
                palette: "peach",
                doodle: "🖍️",
              },
              {
                to: "/art-board?a=safe-place",
                title: "Draw a safe place",
                blurb: "Somewhere your younger self is protected.",
                palette: "sage",
                doodle: "🏡",
              },
              {
                to: "/art-board?a=colour-your-feelings",
                title: "Choose colours for how you feel",
                blurb: "No shapes needed. Just colour.",
                palette: "sky",
                doodle: "🎨",
              },
              {
                to: "/art-board?a=for-my-younger-self",
                title: "Create something for your younger self",
                blurb: "A gift, a place, a promise — whatever arrives.",
                palette: "pink",
                doodle: "🎁",
              },
            ].map((c) => (
              <Link
                key={c.to}
                to={c.to}
                className={`assess-card assess-${c.palette} w-[15.5rem] shrink-0 snap-start p-5 sm:w-[17.5rem]`}
              >
                <span className="assess-card__glow" aria-hidden />
                <span className="assess-card__icon relative" aria-hidden>
                  {c.doodle}
                </span>
                <p className="relative mt-3 font-display text-lg leading-snug text-foreground">
                  {c.title}
                </p>
                <p className="relative mt-1 text-sm text-muted-foreground">{c.blurb}</p>
                <span className="relative mt-3 inline-flex items-center gap-1 text-xs assess-card__meta">
                  Opens the Drawing Board <ArrowUpRight className="h-3.5 w-3.5" aria-hidden />
                </span>
              </Link>
            ))}
          </SwipeRail>
        </section>

        <div className="mt-10">
          <ReflectionCard prompt={REFLECTION_CARDS[2]} palette="teal" doodle="🌤️" />
        </div>

        {/* Childhood joy */}
        <section id="childhood-joy" className="mt-12 scroll-mt-24">
          <SectionHead
            emoji="🌈"
            title="Childhood Joy"
            blurb="What made you feel alive when you were younger? Not everything about this work is heavy."
          />
          <div className="mt-5">
            <ChooserList
              kind="joy_list"
              items={JOY_ITEMS}
              palette="peach"
              addPlaceholder="Something else you loved…"
              saveLabel="Save my Joy List"
              emptyHint="Pick anything that makes you smile, even a little."
            />
          </div>
          <div className="mt-5">
            <PersonalNote>
              <p>
                Mine was drawing on the back of anything I could find, and being outside until
                it got dark. I stopped doing both for about twenty years because they weren't
                "useful". Getting one of them back changed more than any technique did.
              </p>
            </PersonalNote>
          </div>
        </section>

        {/* Comfort menu */}
        <section id="comfort-menu" className="mt-12 scroll-mt-24">
          <SectionHead
            emoji="🕯️"
            title="Comfort Menu"
            blurb="A short list you can reach for on a hard day, so you don't have to think of it then."
          />
          <div className="mt-5">
            <ChooserList
              kind="comfort_menu"
              items={COMFORT_ITEMS}
              palette="lavender"
              addPlaceholder="Something that soothes you…"
              saveLabel="Save my Comfort Menu"
              emptyHint="Small and ordinary counts."
            />
          </div>
        </section>

        <div className="mt-10 grid gap-4 md:grid-cols-2">
          <ReflectionCard prompt={REFLECTION_CARDS[1]} palette="pink" doodle="💗" />
          <ReflectionCard prompt={REFLECTION_CARDS[3]} palette="sage" doodle="🌻" />
        </div>

        <SavedExpressions />

        {/* Where to go next */}
        <section className="mt-12">
          <SectionHead
            emoji="🫶"
            title="A soft landing"
            blurb="If anything opened up today, you don't have to hold it on your own."
          />
          <div className="mt-4 grid gap-3 sm:grid-cols-3">
            <Next to="/grounding" icon={Heart} label="Ground Myself" note="Settle your body first." />
            <Next to="/journal" icon={Mail} label="Recovery Journal" note="Put it somewhere safe." />
            <Next
              to="/art-board"
              icon={PaletteIcon}
              label="Drawing Board"
              note="Let colour do the talking."
            />
          </div>
          <p className="mt-6 inline-flex items-center gap-2 text-xs text-muted-foreground">
            <Sun className="h-3.5 w-3.5" aria-hidden /> Inner Child Work is a space for
            self-reflection, not therapy or clinical treatment.
          </p>
        </section>
      </div>
    </div>
  );
}

function Next({
  to,
  icon: Icon,
  label,
  note,
}: {
  to: string;
  icon: typeof Heart;
  label: string;
  note: string;
}) {
  return (
    <Link
      to={to}
      className="flex items-start gap-3 rounded-2xl border border-border bg-card p-4 transition hover:border-gold/50"
    >
      <Icon className="mt-0.5 h-5 w-5 text-[var(--gold-deep)]" aria-hidden />
      <div>
        <p className="text-sm font-medium text-foreground">{label}</p>
        <p className="text-xs text-muted-foreground">{note}</p>
      </div>
    </Link>
  );
}
